import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormikProps } from 'formik';
import { Button, Modal, Steps, Tabs, Tooltip } from 'antd';
import { findIndex, isArray, isEmpty, isEqual, isNil, merge } from 'lodash';
import { RadioChangeEvent } from 'antd/lib/radio';
import { useNavigate } from 'react-router-dom';
import { AsyncThunk } from '@reduxjs/toolkit';
import cx from 'classnames';

import BaseAlert from 'components/wrappers/BaseAlert';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import {
  createNewAssignment,
  createNewAssignmentFromDraft,
  createNewDraftAssignment,
  getAssignmentPayCodeGroups,
  getAssignmentTypesPayCode,
  getPayCodesDetailsInfo,
  getPayRateTypes,
  rehireAssignmentWithNewCustomer,
  rehireAssignmentWithSameCustomer,
  renewAssignmentWithChanges,
  updateAssignment,
  updateAssignmentByAdmin,
  updateDraftAssignment,
  updateTemplate,
} from 'redux/reducers/assignmentSlice';
import {
  default as W2WorkerInfo,
  WorkerInfoFormValues as W2WorkerInfoFormValues,
} from '../W2Assignments/WorkerInfo';
import {
  default as ICWorkerInfo,
  WorkerInfoFormValues as ICWorkerInfoFormValues,
} from '../ICAssignments/WorkerInfo';
import {
  default as SCWorkerInfo,
  WorkerInfoFormValues as SCWorkerInfoFormValues,
} from '../SCAssignments/WorkerInfo';
import FinancialInfo, {
  FinancialInfoFormValues,
  MultiRateFormValues,
  WorkSiteWorkLocation,
} from '../FinancialInfo';
import SplitInfo, { SplitInfoFormValues } from '../SplitInfo';
import WorksiteLocation, {
  WorksiteLocationFormValues,
} from '../WorksiteLocation';
import CustomerInfo, { CustomerInfoFormValues } from '../CustomerInfo';
import ContactInfo, {
  ContactInfoFormValues,
  isOtherOptionSelected,
} from '../ContactInfo';
import AssignmentInfo, { AssignmentInfoFormValues } from '../AssignmentInfo';
import {
  AssignmentCategoryEnum,
  AssignmentInfoEditModel,
  AssignmentPrimaryDocumentEditModel,
  AssignmentStatusTypeEnum,
  AssignmentTypeEnum,
  BOUsersPriceAgreementRespModel,
  BusinessInfoResponseModel,
  ContractorCompanyModel,
  CreateAssignmentResponse,
  CustomerResponseModel,
  JobCategoryEnum,
  RenewAssignmentEnum,
  RenewAssignmentWithChangesRequest,
  ResourceDownloadResponse,
  ValidationDetailsModel,
  WorkerDetailViewModel,
} from 'api/backofficeApiClient';
import {
  mapAssignmentInfoToRequest,
  mapContactInfoToRequest,
  mapContractorCompanyInformationToRequest,
  mapCustomerInfoFormToRequest,
  mapEffectiveDateToInitialValues,
  mapFinancialInfoSchedulerToRequest,
  mapFinancialInfoToRequest,
  mapInternalReportsToRequest,
  mapMultiRates,
  mapMultiRatesSchedulerToInitialValues,
  mapSplitFormValuesToRequest,
  mapWorkAndBillingLocationToRequest,
  mapWorkerInformationToRequest,
} from '../mapUtils';
import useFormTabs, { TabStatus } from 'hooks/useFormTabs';
import { AssignmentCreationTypeEnum } from '../enum';
import {
  determineInitialTab,
  getModalSubmitName,
  getModalTitle,
  shouldShowTypeSelector,
} from 'utils/assignmentUtils';
import {
  getCorporateTAPrimaryContacts,
  getCorporateTASupervisors,
  getCorporateTATimesheetApprovers,
  getFullUserProfile,
} from 'redux/reducers/accountSlice';
import AssignmentTypeSelection, {
  isAssignmentTypeFormValidForDraft,
} from '../AssignmentTypeSelection';
import {
  ApiErrorMessage,
  CompanyAlreadyExistsError,
  WorkerAlreadyExistsError,
} from 'constants/ErrorMessages';
import { isAddWorkerSelected, workerInitialValues } from '../WorkerSection';
import { companyInitialValues, isAddCompanySelected } from '../CompanySection';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import {
  getAllCities,
  getAllCountries,
  getAllCountryCities,
  getAllStates,
} from 'redux/reducers/regionSlice';
import { getRecruiterDetailsById } from 'redux/reducers/recruiterSlice';
import useModalCloseOnNavigate from 'hooks/useModalCloseOnNavigate';
import { getWorkerTypeByAssignmentType } from 'utils/workerUtils';
import { getMemberWorkers } from 'redux/reducers/workerSlice';
import { getMemberCompanies } from 'redux/reducers/contractorCompanySlice';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import AssignmentTemplate, { TemplateFormValues } from '../AssignmentTemplate';
import { FinancialInfoCalculator } from '../FinancialInfo/calculationUtils';
import FinancialInfoScheduleModal, {
  EffectiveDateMode,
  FinancialSchedulerFormValues,
} from 'components/modals/FinancialInfoScheduleModal';
import { RehireTypeEnum } from 'features/assignment/RehireAssignment/RehireTypeSelectionModal';
import { TabKeys } from './enum';
import {
  getPTOMultiRateDetails,
  updatePrefilledMultiRatesFields,
} from 'utils/multiRates/multiRatesUtils';
import {
  payCodesDetailsInfoSelector,
  payCodesGroupsSelector,
  payCodesSelector,
} from 'redux/selectors/assignmentSelectors';
import { getFilteredPayCodesByJobCriteria } from 'features/assignment/AssignmentDetailsPage/utils';

import styles from './AssignmentModal.module.less';

const { Step } = Steps;

export interface Address {
  address: string | undefined;
}

export interface Addresses {
  worker: Address;
  billing: Address;
}

export type FormType = 'worker' | 'billing';

export type WorkerInfoFormValues = W2WorkerInfoFormValues &
  ICWorkerInfoFormValues &
  SCWorkerInfoFormValues;

export interface AssignmentInitialValues {
  workerInfo: WorkerInfoFormValues;
  customerInfo: CustomerInfoFormValues | ContactInfoFormValues;
  worksiteLocation: WorksiteLocationFormValues;
  assignmentInfo: AssignmentInfoFormValues;
  financialInfo: FinancialInfoFormValues;
  splitInfo: SplitInfoFormValues;
  template?: TemplateFormValues;
  financialScheduler?: FinancialSchedulerFormValues;
}

type Props = {
  onClose: () => void;
  type?: AssignmentTypeEnum;
  isCorporateTA: boolean;
  isRecruiter: boolean;
  initialValues?: AssignmentInitialValues;
  assignmentCreationType?: AssignmentCreationTypeEnum;
  assignmentId?: string;
  onSubmit?: () => void;
  isDraftAvailable?: boolean;
  // used for admin edit
  memberId?: string;
  companyId?: string;
  workerFileInfo?: ResourceDownloadResponse;
  contractorCompanyFileInfo?: ResourceDownloadResponse;
  priceAgreement?: BOUsersPriceAgreementRespModel;
  customers: CustomerResponseModel[];
  assignmentStatus?: AssignmentStatusTypeEnum;
  activeRehireType?: RehireTypeEnum;
  isCustomersLoaded?: boolean;
  renewType?: RenewAssignmentEnum;
  hasActiveScheduledTerminationInFuture?: boolean;
  hasRehireDateRestriction?: boolean;
};

interface SubmitErrorDetailsResponse {
  contactId?: string;
  supervisorId?: string;
  timesheetApproverId?: string;
  isWorkerDuplicated?: boolean;
  isCompanyDuplicated?: boolean;
}

interface AssignmentTabsOptions {
  isEdit: boolean;
  isCorporate: boolean;
  defaultActiveTab: TabKeys;
  isResetTabs?: boolean;
}

interface RequestModelType {
  isDraft: boolean;
}

// in case edit - then there was draft saved before
const getAssignmentTabs = (options: AssignmentTabsOptions) => {
  const { isEdit, isCorporate, defaultActiveTab, isResetTabs } = options;

  return [
    {
      title: 'Assignment type',
      status: isEdit ? TabStatus.Finish : TabStatus.Process,
      isDraftValid: isEdit || isResetTabs,
      draftValidationFunc: isAssignmentTypeFormValidForDraft,
    },
    {
      title: 'Worker information',
      status:
        isEdit && defaultActiveTab === TabKeys.WorkerInfo
          ? TabStatus.Process
          : TabStatus.Wait,
      isDraftValid: true,
      draftValidationFunc: () => true,
    },
    {
      title: isCorporate ? 'Contact information' : 'Customer information',
      status:
        defaultActiveTab === TabKeys.CustomerInfo
          ? TabStatus.Process
          : TabStatus.Wait,
      isDraftValid: true,
      draftValidationFunc: () => true,
    },
    {
      title: 'Worksite location',
      status:
        defaultActiveTab === TabKeys.WorksiteLocation
          ? TabStatus.Process
          : TabStatus.Wait,
      isDraftValid: true,
      draftValidationFunc: () => true,
    },
    {
      title: 'Assignment information',
      status:
        defaultActiveTab === TabKeys.AssignmentInfo
          ? TabStatus.Process
          : TabStatus.Wait,
      isDraftValid: true,
      draftValidationFunc: () => true,
    },
    {
      title: 'Financial information',
      status:
        defaultActiveTab === TabKeys.FinancialInfo
          ? TabStatus.Process
          : TabStatus.Wait,
      isDraftValid: true,
      draftValidationFunc: () => true,
    },
    {
      title: 'Split information',
      status:
        defaultActiveTab === TabKeys.SplitInfo
          ? TabStatus.Process
          : TabStatus.Wait,
      isDraftValid: true,
      draftValidationFunc: () => true,
    },
  ];
};

type WorkerInfoFormikProps = FormikProps<WorkerInfoFormValues>;
type CustomerInfoFormikProps = FormikProps<
  CustomerInfoFormValues | ContactInfoFormValues
>;
type WorksiteLocationFormikProps = FormikProps<WorksiteLocationFormValues>;
type AssignmentInfoFormikProps = FormikProps<AssignmentInfoFormValues>;
type FinancialInfoFormikProps = FormikProps<FinancialInfoFormValues>;
type SplitInfoFormikProps = FormikProps<SplitInfoFormValues>;
type TemplateFormikProps = FormikProps<TemplateFormValues>;
type FinancialSchedulerFormikProps = FormikProps<FinancialSchedulerFormValues>;

export default function AssignmentModal(props: Props) {
  const {
    onClose,
    type,
    isCorporateTA,
    assignmentCreationType = AssignmentCreationTypeEnum.Create,
    initialValues,
    assignmentId,
    onSubmit,
    isDraftAvailable = true,
    memberId,
    companyId,
    workerFileInfo,
    contractorCompanyFileInfo,
    priceAgreement,
    customers,
    assignmentStatus,
    isRecruiter,
    activeRehireType,
    isCustomersLoaded,
    renewType,
    hasActiveScheduledTerminationInFuture,
    hasRehireDateRestriction,
  } = props;

  const navigate = useNavigate();
  const [predefinedInitialValues, setPredefinedInitialValues] = useState<
    AssignmentInitialValues | undefined
  >(initialValues);
  const [addresses, setAddresses] = useState<Addresses>({
    worker: { address: undefined },
    billing: { address: undefined },
  });
  const isAdminRole = useAppSelector(isAdminSelector);
  const payCodesDetails = useAppSelector(payCodesDetailsInfoSelector);
  const payCodes = useAppSelector(payCodesSelector);
  const payCodesGroups = useAppSelector(payCodesGroupsSelector);
  const payCodesDetailsInfo = useAppSelector(payCodesDetailsInfoSelector);
  const isActiveAssignment =
    assignmentStatus === AssignmentStatusTypeEnum.Active;

  const isRehireMode =
    assignmentCreationType === AssignmentCreationTypeEnum.Rehire;
  const isEditRehireMode =
    assignmentCreationType === AssignmentCreationTypeEnum.EditRehire;
  const isRehireOrEditRehireMode = isRehireMode || isEditRehireMode;

  const isRehireWithNewCustomer =
    isRehireOrEditRehireMode &&
    (activeRehireType === RehireTypeEnum.NewCustomer ||
      renewType === RenewAssignmentEnum.WithChangeCustomer);
  const isRehireWithSameCustomer =
    isRehireOrEditRehireMode &&
    (activeRehireType === RehireTypeEnum.SameCustomer ||
      renewType === RenewAssignmentEnum.WithChanges);
  const hasRehireModeAndDateRestriction =
    isRehireOrEditRehireMode && hasRehireDateRestriction;

  const isRenewMode =
    assignmentCreationType === AssignmentCreationTypeEnum.Renew;
  const isEditRenewMode =
    assignmentCreationType === AssignmentCreationTypeEnum.EditRenew;
  const isRenewOrEditRenewMode = isRenewMode || isEditRenewMode;
  const hasRenewModeAndDateRestriction =
    isRenewOrEditRenewMode && hasActiveScheduledTerminationInFuture;

  const isTemplateMode =
    assignmentCreationType === AssignmentCreationTypeEnum.Template;
  const isTemplateEditMode =
    assignmentCreationType === AssignmentCreationTypeEnum.EditTemplate;

  const isRenewOrRehireMode =
    isRenewOrEditRenewMode || isRehireOrEditRehireMode;

  const isEditMode =
    (!!isAdminRole && isEditRenewMode) ||
    (!!isAdminRole && isEditRehireMode) ||
    isTemplateMode ||
    isTemplateEditMode ||
    assignmentCreationType === AssignmentCreationTypeEnum.Edit;
  const isCreateMode =
    assignmentCreationType === AssignmentCreationTypeEnum.Create;
  const hasActiveAssignmentRestriction =
    (isActiveAssignment && !isAdminRole) || false;

  const isTypeSelectorStepVisible = shouldShowTypeSelector({
    isCreateMode,
    isAdminRole,
    isEditMode,
    hasActiveAssignmentRestriction,
    isRenewMode: isRenewOrEditRenewMode,
    isRehireMode: isRehireOrEditRehireMode,
  });

  const defaultActiveTab = determineInitialTab({
    isTypeSelectorStepVisible,
    isEditRenewMode,
    isEditRehireMode,
    isTemplateEditMode,
  });

  const dispatch = useAppDispatch();
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerResponseModel>();
  const [selectedAssignmentType, setSelectedAssignmentType] = useState(
    type || null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const loadedBusinessInfo = useRef<BusinessInfoResponseModel>();
  const [workers, setWorkers] = useState<WorkerDetailViewModel[]>([]);
  const [isWorkersLoaded, setIsWorkersLoaded] = useState(false);
  const [companies, setCompanies] = useState<ContractorCompanyModel[]>([]);
  const [isCompaniesLoaded, setIsCompaniesLoaded] = useState(false);
  const [isTemplateErrors, setIsTemplateErrors] = useState(false);
  const [initialFinancialSchedulerValues, setInitialFinancialSchedulerValues] =
    useState<FinancialSchedulerFormValues | undefined>(
      initialValues?.financialScheduler,
    );
  const [
    isFinancialInfoSchedulerModalVisible,
    setIsFinancialInfoSchedulerModalVisible,
  ] = useState(false);

  const isW2Assignment =
    selectedAssignmentType === AssignmentTypeEnum.EEAssignment;
  const w2InfoList = priceAgreement?.w2InfoList;

  const isVisibleTemplateSection = !isAdminRole || isTemplateEditMode;

  useModalCloseOnNavigate(onClose);

  const typeSelectionFormRef: any = useRef();
  const workerInfoFormRef: any = useRef();
  const customerInfoFormRef: any = useRef();
  const worksiteLocationFormRef: any = useRef();
  const assignmentInfoFormRef: any = useRef();
  const financialInfoFormRef: any = useRef();
  const splitInfoFormRef: any = useRef();
  const templateFormRef: any = useRef();
  const financialSchedulerFormRef: any = useRef();

  const handleAddressChange = (
    formType: FormType,
    field: keyof Address,
    value: string | undefined,
  ) => {
    setAddresses((prevAddresses) => ({
      ...prevAddresses,
      [formType]: {
        ...prevAddresses[formType],
        [field]: value,
      },
    }));
  };

  const handleOpenFinancialSchedulerModal = () => {
    setIsFinancialInfoSchedulerModalVisible(true);
  };

  const handleCloseFinancialSchedulerModal = () => {
    setIsFinancialInfoSchedulerModalVisible(false);
    resetFormSubmitting();
  };

  const [tabs, onFormChange, resetTabs, activeTab, setActiveTab] = useFormTabs(
    getAssignmentTabs({
      isEdit: !!predefinedInitialValues,
      isCorporate: !!isCorporateTA,
      defaultActiveTab,
    }),
    defaultActiveTab,
  );

  const initialMBPRate = useMemo(() => {
    if (!selectedAssignmentType) return;

    const initialMBpRate = initialValues?.financialInfo.mbpRate;
    const workerInfoForm: WorkerInfoFormikProps = workerInfoFormRef.current;
    const worksiteLocationForm: WorksiteLocationFormikProps =
      worksiteLocationFormRef.current;

    const mbpRateCalculated = FinancialInfoCalculator.getInitialMbpRate(
      workerInfoForm?.values,
      worksiteLocationForm?.values,
      selectedAssignmentType,
      priceAgreement,
    );
    return initialMBpRate || mbpRateCalculated;
    // eslint-disable-next-line
  }, [
    initialValues?.financialInfo.mbpRate,
    selectedAssignmentType,
    priceAgreement,
    workerInfoFormRef.current?.values,
    worksiteLocationFormRef.current?.values?.work,
  ]);

  const isDraftValid =
    tabs.every((tab) => tab.isDraftValid) && !isTemplateErrors;

  const workerLocation = useMemo(
    () =>
      FinancialInfoCalculator.getWorkerLocation(
        isW2Assignment,
        workerInfoFormRef?.current?.values,
      ),
    // eslint-disable-next-line
    [isW2Assignment, workerInfoFormRef?.current?.values],
  );

  const getWorksiteWorkLocation = useCallback(() => {
    const worksiteLocationForm: WorksiteLocationFormikProps =
      worksiteLocationFormRef.current;

    if (!worksiteLocationForm) return null;

    return worksiteLocationForm?.values?.work?.locationSameAsMain
      ? workerLocation
      : {
          country: worksiteLocationForm?.values?.work?.country || undefined,
          state: worksiteLocationForm?.values?.work?.state || undefined,
          internationalStateName:
            worksiteLocationForm?.values?.work?.internationalStateName ||
            undefined,
          city: worksiteLocationForm?.values?.work?.city || undefined,
          internationalCityName:
            worksiteLocationForm?.values?.work?.internationalCityName ||
            undefined,
          address: worksiteLocationForm?.values?.work?.address || undefined,
          zip: worksiteLocationForm?.values?.work?.zip || undefined,
        };
  }, [workerLocation]);

  const workSiteWorkLocation: WorkSiteWorkLocation | null =
    getWorksiteWorkLocation();

  const w2InfoByWorkSiteWorkLocation = useMemo(() => {
    if (
      !w2InfoList?.length ||
      !workSiteWorkLocation?.state ||
      !isW2Assignment
    ) {
      return;
    }

    const w2InfoStateIds = w2InfoList.map((w2Info) => ({
      ...w2Info,
      placementStates: (w2Info.placementStates || []).map((state) => state.id),
    }));

    return w2InfoStateIds.find((w2Info) =>
      w2Info.placementStates.includes(workSiteWorkLocation.state),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w2InfoList, workSiteWorkLocation?.state, selectedAssignmentType]);

  // TODO: was done fix because of a lack of time, should be redone
  useEffect(() => {
    if (
      isCorporateTA &&
      (assignmentCreationType === AssignmentCreationTypeEnum.Create ||
        isAdminRole)
    ) {
      loadFullProfile();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(getAllCountries());
    dispatch(getAllCountryCities());
    dispatch(getAllStates());
    dispatch(getAllCities());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPayRateTypes(isAdminRole));
    dispatch(getPayCodesDetailsInfo(isAdminRole));
    dispatch(getAssignmentTypesPayCode(isAdminRole));
    dispatch(getAssignmentPayCodeGroups(isAdminRole));
  }, [dispatch, isAdminRole]);

  useEffect(() => {
    loadWorkers();
    loadMemberCompanies();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const customer = customers.find(
      (customer) =>
        customer.customerId ===
        (predefinedInitialValues?.customerInfo as CustomerInfoFormValues)
          ?.generalInfo?.customer,
    );

    setSelectedCustomer(customer);
    // eslint-disable-next-line
  }, [
    customers,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (predefinedInitialValues?.customerInfo as CustomerInfoFormValues)
      ?.generalInfo?.customer,
    isCustomersLoaded,
  ]);

  const loadFullProfile = async () => {
    const correctAction: any = isAdminRole
      ? getRecruiterDetailsById
      : getFullUserProfile;

    const result = await dispatch(
      correctAction(isAdminRole ? memberId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      loadedBusinessInfo.current = result.payload.businessInfo;
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadWorkers = useCallback(async () => {
    const result = await dispatch(
      getMemberWorkers({
        isAdmin: isAdminRole,
        companyId: companyId as string,
      }),
    );

    if (getMemberWorkers.fulfilled.match(result)) {
      setWorkers(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsWorkersLoaded(true);
  }, [dispatch, isAdminRole, companyId]);

  const loadMemberCompanies = useCallback(async () => {
    const result = await dispatch(
      getMemberCompanies({
        isAdmin: isAdminRole,
        companyId: companyId as string,
      }),
    );

    if (getMemberCompanies.fulfilled.match(result)) {
      setCompanies(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsCompaniesLoaded(true);
  }, [dispatch, isAdminRole, companyId]);

  const setupBillingLocation = () => {
    if (!worksiteLocationFormRef?.current || !loadedBusinessInfo.current)
      return;

    const { location } = loadedBusinessInfo.current;

    const worksiteLocationForm: WorksiteLocationFormikProps =
      worksiteLocationFormRef.current;

    worksiteLocationForm.setFieldValue(
      'billing.state',
      location?.stateId || undefined,
      false,
    );
    worksiteLocationForm.setFieldValue(
      'billing.city',
      location?.city || undefined,
      false,
    );
    worksiteLocationForm.setFieldValue(
      'billing.address',
      location?.address,
      false,
    );

    worksiteLocationForm.setFieldValue(
      'billing.zip',
      location?.zip || '',
      false,
    );
  };

  const setupWorkerType = (assignmentType: AssignmentTypeEnum) => {
    const workerType = getWorkerTypeByAssignmentType(assignmentType);
    const workerInfoForm: WorkerInfoFormikProps = workerInfoFormRef.current;

    workerInfoForm.setFieldValue('worker.workerType', workerType, false);
  };

  const getCurrentFormRef = () => {
    switch (activeTab) {
      case TabKeys.TypeSelector:
        return typeSelectionFormRef.current;

      case TabKeys.WorkerInfo:
        return workerInfoFormRef.current;

      case TabKeys.CustomerInfo:
        return customerInfoFormRef.current;

      case TabKeys.WorksiteLocation:
        return worksiteLocationFormRef.current;

      case TabKeys.AssignmentInfo:
        return assignmentInfoFormRef.current;

      case TabKeys.FinancialInfo:
        return financialInfoFormRef.current;

      case TabKeys.SplitInfo:
        return splitInfoFormRef.current;
    }
  };

  const getTabClassNames = (tabKey: TabKeys, status: TabStatus) => {
    return {
      active: activeTab === tabKey,
      success: status === TabStatus.Process || status === TabStatus.Finish,
      error: status === TabStatus.Error,
    };
  };

  const getTabStatus = (tabKey: TabKeys) => {
    const lastFinishIndex =
      findIndex(tabs, (tab) => tab.status !== TabStatus.Finish) - 1;

    if (tabs[tabKey].status === TabStatus.Finish && lastFinishIndex >= 0) {
      if (
        tabKey > lastFinishIndex ||
        ((tabs[tabKey + 1] || {}).status === TabStatus.Wait &&
          tabKey + 1 !== activeTab)
      ) {
        return TabStatus.Process;
      }
    }

    if (
      activeTab === tabKey &&
      tabs[tabKey].status !== TabStatus.Error &&
      tabs[tabKey].status !== TabStatus.Finish
    )
      return TabStatus.Process;

    return tabs[tabKey].status;
  };

  const validateCurrentForm = async () => {
    await getCurrentFormRef()?.submitForm();

    resetFormSubmitting();
  };

  const handleTabChange = async (tab: TabKeys) => {
    if (tab === activeTab) return;

    await validateCurrentForm();

    if (!selectedAssignmentType) return;

    setActiveTab(tab);
  };

  const handleNextStep = async () => {
    await validateCurrentForm();

    if (!selectedAssignmentType) return;

    setActiveTab((step) => step + 1);
  };

  const handlePrevStep = async () => {
    await validateCurrentForm();

    setActiveTab((step) => step - 1);
  };

  const getCurrentFormValues = () => {
    const workerInfoForm: WorkerInfoFormikProps = workerInfoFormRef.current;
    const customerInfoForm: CustomerInfoFormikProps =
      customerInfoFormRef.current;
    const worksiteLocationForm: WorksiteLocationFormikProps =
      worksiteLocationFormRef.current;
    const assignmentInfoForm: AssignmentInfoFormikProps =
      assignmentInfoFormRef.current;
    const financialInfoForm: FinancialInfoFormikProps =
      financialInfoFormRef.current;
    const splitInfoForm: SplitInfoFormikProps = splitInfoFormRef.current;
    const templateForm: TemplateFormikProps = templateFormRef.current;
    const financialSchedulerForm: FinancialSchedulerFormikProps =
      financialSchedulerFormRef.current;

    let currentFormValues: AssignmentInitialValues = {
      workerInfo: workerInfoForm.values,
      customerInfo: customerInfoForm.values,
      worksiteLocation: worksiteLocationForm.values,
      assignmentInfo: assignmentInfoForm.values,
      financialInfo: financialInfoForm.values,
      splitInfo: splitInfoForm.values,
      financialScheduler: financialSchedulerForm?.values,
    };

    if (isVisibleTemplateSection) {
      currentFormValues.template = templateForm.values;
    }

    return currentFormValues;
  };

  const updateAssignmentValuesWithFilteredMultiRates = (
    jobCategory: JobCategoryEnum | null,
  ) => {
    const currentFormValues = getCurrentFormValues();
    const previousJobCategoryValueJobCategoryValue =
      currentFormValues.assignmentInfo.jobCategory;

    const hasRestrictionForMultiRatesByJobCategory = payCodesGroups?.find(
      (payCodesGroup) =>
        payCodesGroup.jobCategory === jobCategory ||
        payCodesGroup.jobCategory === previousJobCategoryValueJobCategoryValue,
    );

    if (!hasRestrictionForMultiRatesByJobCategory) return;

    const filteredMultiRates = filterMultiRatesByAssignmentType(
      currentFormValues.financialInfo.multiRates,
      selectedAssignmentType,
      jobCategory,
    );

    const assignmentValues: AssignmentInitialValues = {
      ...currentFormValues,
      assignmentInfo: {
        ...currentFormValues.assignmentInfo,
        jobCategory: jobCategory,
      },
      financialInfo: {
        ...currentFormValues.financialInfo,
        multiRates: filteredMultiRates,
      },
    };

    setPredefinedInitialValues(assignmentValues);
  };

  const getCommonValuesByAssignmentType = (
    assignmentType: AssignmentTypeEnum,
  ): AssignmentInitialValues => {
    const currentFormValues = getCurrentFormValues();
    const filteredMultiRates = filterMultiRatesByAssignmentType(
      currentFormValues.financialInfo.multiRates,
      assignmentType,
      currentFormValues.assignmentInfo.jobCategory,
    );

    const assignmentValues: AssignmentInitialValues = {
      ...currentFormValues,
      assignmentInfo: {
        ...currentFormValues.assignmentInfo,
        // Employmenet type is absent on the IC & SC assignment types
        employmentType: null,
      },
      financialInfo: {
        ...currentFormValues.financialInfo,
        // Pay type is absent on the IC & SC assignment types
        payType: null,
        multiRates: filteredMultiRates,
      },
      workerInfo: {
        ...currentFormValues.workerInfo,
        // Worker & contractor company sections depend on assignment type
        // So sections should be dropped
        taxId: null,
        workerFileKey: [],
        company: companyInitialValues,
        worker: workerInitialValues,
      },
    };

    return assignmentValues;
  };

  const filterMultiRatesByAssignmentType = (
    multiRatesValues?: MultiRateFormValues[],
    assignmentType?: AssignmentTypeEnum | null,
    jobCategory?: JobCategoryEnum | null,
  ): MultiRateFormValues[] | undefined => {
    const relevantPayCodes = getFilteredPayCodesByJobCriteria({
      payCodesDetails: payCodes,
      assignmentType,
      jobCategory,
      payCodesGroups,
      payCodesDetailsInfo,
    });

    const relevantPayCodeDetails = relevantPayCodes || [];
    const availableRateTypeIds = new Set(
      relevantPayCodeDetails.map((code) => code.payRateTypeId),
    );
    return multiRatesValues?.filter((rate) =>
      availableRateTypeIds.has(rate.payTypeId),
    );
  };

  const handleAssignmentTypeChange = (event: RadioChangeEvent) => {
    const newAssignmentType: AssignmentTypeEnum = event.target.value;

    setSelectedAssignmentType(() => {
      if (isEditMode) {
        const commonValuesByType =
          getCommonValuesByAssignmentType(newAssignmentType);
        setPredefinedInitialValues(commonValuesByType);
      } else {
        setSelectedCustomer(undefined);
        setPredefinedInitialValues(undefined);
      }

      return newAssignmentType;
    });

    if (!isEditMode) {
      resetForm();
    }

    setTimeout(() => {
      setupWorkerType(newAssignmentType);
      setupBillingLocation();

      resetTabs(
        getAssignmentTabs({
          isResetTabs: true,
          isEdit: !!predefinedInitialValues,
          isCorporate: !!isCorporateTA,
          defaultActiveTab,
        }),
      );

      // Set payType field untouched on the edit mode for admin on the W2 assignment
      // ---> GeneralContractInfo component:
      // ---> calculateProfitRateValue is invoked on workSiteWorkLocation.state change
      // ---> it occurs after resetting tabs on the assignment type change
      // ---> that causes calling onChange in the FormikEffect
      // ---> that leads to setting error for this field in the formik
      // ---> as a result financial info tab is red-colored (but should be in the 'Wait' state)
      if (isEditMode && newAssignmentType === AssignmentTypeEnum.EEAssignment) {
        const financialInfoForm: FinancialInfoFormikProps =
          financialInfoFormRef.current;
        financialInfoForm.setFieldTouched('payType', false, false);
      }
    }, 500);
  };

  const isFormDirty = () => {
    const arr = [
      typeSelectionFormRef.current?.dirty,
      workerInfoFormRef.current?.dirty,
      customerInfoFormRef.current?.dirty,
      worksiteLocationFormRef.current?.dirty,
      assignmentInfoFormRef.current?.dirty,
      financialInfoFormRef.current?.dirty,
      splitInfoFormRef.current?.dirty,
    ];

    if (isVisibleTemplateSection) {
      arr.push(templateFormRef.current?.dirty);
    }
    return arr.some(Boolean);
  };

  const isFormValid = () => {
    const arr = [
      typeSelectionFormRef.current?.isValid,
      workerInfoFormRef.current?.isValid,
      customerInfoFormRef.current?.isValid,
      worksiteLocationFormRef.current?.isValid,
      assignmentInfoFormRef.current?.isValid,
      financialInfoFormRef.current?.isValid,
      splitInfoFormRef.current?.isValid,
    ];

    if (isVisibleTemplateSection) {
      arr.push(templateFormRef.current?.isValid);
    }
    return arr.every(Boolean);
  };

  const isFormSubmitting = () => {
    const arr = [
      typeSelectionFormRef.current?.isSubmitting,
      workerInfoFormRef.current?.isSubmitting,
      customerInfoFormRef.current?.isSubmitting,
      worksiteLocationFormRef.current?.isSubmitting,
      assignmentInfoFormRef.current?.isSubmitting,
      financialInfoFormRef.current?.isSubmitting,
      splitInfoFormRef.current?.isSubmitting,
    ];

    if (isVisibleTemplateSection) {
      arr.push(templateFormRef.current?.isSubmitting);
    }
    return arr.every(Boolean);
  };

  const resetFormSubmitting = () => {
    const arr = [
      typeSelectionFormRef?.current,
      workerInfoFormRef?.current,
      customerInfoFormRef?.current,
      worksiteLocationFormRef?.current,
      assignmentInfoFormRef?.current,
      financialInfoFormRef?.current,
      splitInfoFormRef?.current,
    ];

    if (isVisibleTemplateSection) {
      arr.push(templateFormRef?.current);
    }
    arr.forEach((form) => form?.setSubmitting(false));
  };

  const resetForm = () => {
    workerInfoFormRef.current.resetForm();
    customerInfoFormRef.current.resetForm();
    worksiteLocationFormRef.current.resetForm();
    assignmentInfoFormRef.current.resetForm();
    financialInfoFormRef.current.resetForm();
    splitInfoFormRef.current.resetForm();
  };

  const handleModalCancel = () => {
    if (isFormSubmitting()) return;

    if (isFormDirty()) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const handlePrimaryContactRemovedFromAccount = (
    customerInfoForm: FormikProps<ContactInfoFormValues>,
  ) => {
    const {
      primaryContacts: { bestContacts },
    } = customerInfoForm.values;

    dispatch(
      getCorporateTAPrimaryContacts({
        isAdmin: isAdminRole,
        companyId,
      }),
    );

    customerInfoForm.setFieldValue('primaryContacts.bestContacts', [], true);

    const isOtherPrimaryContactSelected = bestContacts.some((value) =>
      isOtherOptionSelected(value),
    );

    if (isOtherPrimaryContactSelected) {
      customerInfoForm.setFieldValue(
        'primaryContacts.newPrimaryContact',
        {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          title: '',
        },
        false,
      );
      customerInfoForm.setFieldTouched(
        'primaryContacts.newPrimaryContact',
        false,
        false,
      );
    }
  };

  const handleSupervisorContactRemovedFromAccount = (
    customerInfoForm: FormikProps<ContactInfoFormValues>,
  ) => {
    const {
      supervisorInfo: { supervisor },
    } = customerInfoForm.values;

    dispatch(
      getCorporateTASupervisors({
        isAdmin: isAdminRole,
        companyId,
      }),
    );

    customerInfoForm.setFieldValue('supervisorInfo.supervisor', '', true);

    const isOtherSupervisorContactSelected = isOtherOptionSelected(supervisor);

    if (isOtherSupervisorContactSelected) {
      customerInfoForm.setFieldValue(
        'supervisorInfo.newSupervisor',
        {
          firstName: '',
          lastName: '',
          title: '',
          email: '',
          phone: '',
        },
        false,
      );
      customerInfoForm.setFieldTouched(
        'supervisorInfo.newSupervisor',
        false,
        false,
      );
    }
  };

  const handleTimesheetApproverContactRemovedFromAccount = (
    customerInfoForm: FormikProps<ContactInfoFormValues>,
  ) => {
    const {
      timesheetApprover: { approver, sameAsSupervisor, notApplicable },
    } = customerInfoForm.values;

    dispatch(
      getCorporateTATimesheetApprovers({
        isAdmin: isAdminRole,
        companyId,
      }),
    );

    customerInfoForm.setFieldValue('timesheetApprover.approver', '', true);

    const isOtherTimesheetApproverSelected = isOtherOptionSelected(approver);

    if (isOtherTimesheetApproverSelected) {
      customerInfoForm.setFieldValue(
        'timesheetApprover.newTimesheetApprover',
        {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          sameAsSupervisor,
          notApplicable,
        },
        false,
      );
      customerInfoForm.setFieldTouched(
        'timesheetApprover.newTimesheetApprover',
        false,
        false,
      );
    }
  };

  const handleWorkerAlreadyExistsError = async (
    workerInfoForm: FormikProps<WorkerInfoFormValues>,
  ) => {
    const { worker } = workerInfoForm.values;

    const isAddOptionSelected = isAddWorkerSelected(worker.workerId);

    if (isAddOptionSelected) {
      workerInfoForm.setFieldTouched('worker.email');
      workerInfoForm.setFieldTouched('worker.lastName');
      workerInfoForm.setFieldTouched('worker.workerType');
      workerInfoForm.setFieldValue(
        'worker._formError',
        WorkerAlreadyExistsError,
      );
    }
  };

  const handleCompanyAlreadyExistsError = (
    workerInfoForm: FormikProps<WorkerInfoFormValues>,
  ) => {
    const {
      company: { companyId },
    } = workerInfoForm.values;

    const isAddSelected = isAddCompanySelected(companyId);

    if (isAddSelected) {
      workerInfoForm.setFieldTouched('company.email');
      workerInfoForm.setFieldTouched('company.companyName');
      workerInfoForm.setFieldValue(
        'company._formError',
        CompanyAlreadyExistsError,
      );
    }
  };

  const onAssignmentSubmitError = (
    errorDetails?: SubmitErrorDetailsResponse,
  ) => {
    const customerInfoForm: FormikProps<ContactInfoFormValues> =
      customerInfoFormRef.current;
    const workerInfoForm: FormikProps<WorkerInfoFormValues> =
      workerInfoFormRef.current;

    if (!isNil(errorDetails?.contactId)) {
      handlePrimaryContactRemovedFromAccount(customerInfoForm);
    }

    if (!isNil(errorDetails?.supervisorId)) {
      handleSupervisorContactRemovedFromAccount(customerInfoForm);
    }

    if (!isNil(errorDetails?.timesheetApproverId)) {
      handleTimesheetApproverContactRemovedFromAccount(customerInfoForm);
    }

    if (!isNil(errorDetails?.isWorkerDuplicated)) {
      handleWorkerAlreadyExistsError(workerInfoForm);
    }

    if (!isNil(errorDetails?.isCompanyDuplicated)) {
      handleCompanyAlreadyExistsError(workerInfoForm);
    }
  };

  // In case if user is trying to save an assignment with already deleted account contact(s)
  // or duplicated worker / company
  const handleSubmitError = (result: any) => {
    if (isArray(result.payload) && result.payload.length) {
      result.payload.map((error: ValidationDetailsModel) => {
        BaseAlert.error(error.errorMessage || ApiErrorMessage);
        return onAssignmentSubmitError(error.errorDetails);
      });
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    resetFormSubmitting();
  };

  const onAssignmentSubmit = async () => {
    const typeSelectionForm = typeSelectionFormRef.current;
    const workerInfoForm: WorkerInfoFormikProps = workerInfoFormRef.current;
    const customerInfoForm: CustomerInfoFormikProps =
      customerInfoFormRef.current;
    const worksiteLocationForm: WorksiteLocationFormikProps =
      worksiteLocationFormRef.current;
    const assignmentInfoForm: AssignmentInfoFormikProps =
      assignmentInfoFormRef.current;
    const financialInfoForm: FinancialInfoFormikProps =
      financialInfoFormRef.current;
    const splitInfoForm: SplitInfoFormikProps = splitInfoFormRef.current;
    const templateForm: TemplateFormikProps = templateFormRef.current;

    await Promise.all([
      typeSelectionForm.submitForm(),
      workerInfoForm.submitForm(),
      customerInfoForm.submitForm(),
      worksiteLocationForm.submitForm(),
      assignmentInfoForm.submitForm(),
      financialInfoForm.submitForm(),
      splitInfoForm.submitForm(),
      templateForm?.submitForm(),
    ]);

    if (!isFormValid() || isDraftLoading) return;

    if (isAdminRole && !isRenewOrRehireMode) {
      return Modal.confirm({
        title:
          'The assignment data will be rewritten with the info that you have specified. Are you sure that everything is correct?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          if (hasFinancialSchedulerChanges()) return;

          return handleSubmit();
        },
        onCancel() {},
      });
    }
    const isCheckFinancialScheduler =
      isEditMode && !isTemplateEditMode && !isTemplateMode;

    isCheckFinancialScheduler
      ? hasFinancialSchedulerChanges() || (await handleSubmit())
      : await handleSubmit();
  };

  const hasFinancialSchedulerChanges = () => {
    const currentFormValues = getCurrentFormValues();

    const oldFinancialInfoData = predefinedInitialValues?.financialInfo;
    const currentFinancialInfoData = currentFormValues?.financialInfo;

    if (isEqual(oldFinancialInfoData, currentFinancialInfoData)) {
      return false;
    }

    const predefinedFinancialInfoSchedulerData =
      predefinedInitialValues?.financialScheduler;

    const multiRatesForm = currentFormValues.financialInfo.multiRates || [];
    const predefinedMultiRates = updatePrefilledMultiRatesFields(
      multiRatesForm,
      predefinedFinancialInfoSchedulerData?.multiRates || [],
    );

    const updatedFinancialSchedulerData = {
      ...predefinedFinancialInfoSchedulerData,
      multiRates: predefinedMultiRates,
    };

    const values = mapEffectiveDateToInitialValues(
      oldFinancialInfoData,
      currentFinancialInfoData,
      updatedFinancialSchedulerData,
    );

    if (!values) {
      return false;
    }

    onSetFinancialScheduler(values);
    handleOpenFinancialSchedulerModal();
    return true;
  };

  const handleSubmit = async () => {
    const data:
      | AssignmentPrimaryDocumentEditModel
      | RenewAssignmentWithChangesRequest = getAssignmentRequestModel({
      isDraft: false,
    });

    let requestData: any;
    let correctAction: AsyncThunk<CreateAssignmentResponse, any, {}>;
    let successText: string;

    switch (assignmentCreationType) {
      case AssignmentCreationTypeEnum.Edit:
        correctAction = isAdminRole
          ? updateAssignmentByAdmin
          : updateAssignment;
        requestData = {
          assignmentId,
          data,
        };
        successText = 'Assignment has been updated.';
        break;

      case AssignmentCreationTypeEnum.Renew:
        correctAction = renewAssignmentWithChanges;
        requestData = { data, isAdminRole };
        successText =
          'Renewal request submitted. Upon approval your changes will become visible here.';
        break;

      case AssignmentCreationTypeEnum.EditRenew:
        correctAction = updateAssignmentByAdmin;
        requestData = {
          assignmentId,
          data,
        };
        successText = 'Renewal request has been updated.';
        break;

      case AssignmentCreationTypeEnum.Rehire:
        const isRehireWithSameCustomer =
          activeRehireType === RehireTypeEnum.SameCustomer;
        correctAction = isRehireWithSameCustomer
          ? rehireAssignmentWithSameCustomer
          : rehireAssignmentWithNewCustomer;
        requestData = { data, isAdminRole };
        successText = isRehireWithSameCustomer
          ? 'Rehire request submitted. Upon approval your changes will become visible here.'
          : 'Rehire request submitted.';
        break;

      case AssignmentCreationTypeEnum.EditRehire:
        correctAction = updateAssignmentByAdmin;
        requestData = {
          assignmentId,
          data,
        };
        successText = 'Rehire request has been updated.';
        break;

      case AssignmentCreationTypeEnum.EditTemplate:
        correctAction = updateTemplate;
        requestData = {
          assignmentId,
          data,
        };
        successText = 'Assignment template has been updated.';
        break;

      case AssignmentCreationTypeEnum.EditDraft:
        correctAction = createNewAssignmentFromDraft;
        requestData = {
          assignmentId,
          data,
        };
        successText = 'New assignment request has been submitted.';
        break;

      default:
        // AssignmentCreationTypeEnum.Create is a default
        correctAction = createNewAssignment;
        requestData = data;
        successText = 'New assignment request has been submitted.';
        break;
    }

    setIsLoading(true);
    const result = await dispatch(correctAction(requestData));
    setIsLoading(false);

    if (correctAction.fulfilled.match(result)) {
      BaseAlert.success(successText);

      // Replace assignmentId with assigmentKey after migrating from draft to request
      // https://mybasepay.atlassian.net/browse/MBO-2394
      if (assignmentCreationType === AssignmentCreationTypeEnum.EditDraft) {
        navigate(`/assignment/${result.payload.assignmentKey}`, {
          replace: true,
        });
      } else {
        onClose();
        onSubmit?.();
      }
    } else {
      handleSubmitError(result);
    }
  };

  const handleDraftSubmit = async () => {
    if (isFormSubmitting() || !isDraftValid || isDraftLoading) return;

    const data: AssignmentPrimaryDocumentEditModel = getAssignmentRequestModel({
      isDraft: true,
    });

    let requestData: any;
    let correctAction: any;
    let successText: string;

    switch (assignmentCreationType) {
      case AssignmentCreationTypeEnum.EditDraft:
        correctAction = updateDraftAssignment;
        requestData = {
          data,
          assignmentId,
        };
        successText = 'Draft of assignment creation request updated.';
        break;

      default:
        // AssignmentCreationTypeEnum.Create is a default
        correctAction = createNewDraftAssignment;
        requestData = data;
        successText = 'Assignment draft created and saved in Drafts.';
        break;
    }

    setIsDraftLoading(true);
    const result = await dispatch(correctAction(requestData));
    setIsDraftLoading(false);

    if (correctAction.fulfilled.match(result)) {
      BaseAlert.success(successText);
      onClose();

      onSubmit?.();
    } else {
      handleSubmitError(result);
    }
  };

  const handleCustomerChange = (customer?: CustomerResponseModel) => {
    if (!customer) return;

    setSelectedCustomer(customer);

    const worksiteLocationForm: WorksiteLocationFormikProps =
      worksiteLocationFormRef?.current;

    if (worksiteLocationForm?.values?.billing?.locationSameAsWork) {
      return;
    }

    // without setTimeout the validation on the worksitelocation component is incorrect
    setTimeout(() => {
      worksiteLocationForm.setFieldValue(
        'billing.country',
        customer.billingLocation?.countryId || undefined,
      );
      worksiteLocationForm.setFieldValue(
        'billing.state',
        customer.billingLocation?.stateId || undefined,
      );
      worksiteLocationForm.setFieldValue(
        'billing.internationalStateName',
        customer.billingLocation?.internationalStateName || undefined,
      );
      worksiteLocationForm.setFieldValue(
        'billing.city',
        customer.billingLocation?.city || undefined,
      );
      worksiteLocationForm.setFieldValue(
        'billing.internationalCityName',
        customer.billingLocation?.internationalCityName || undefined,
      );
      worksiteLocationForm.setFieldValue(
        'billing.address',
        customer.billingLocation?.address || undefined,
      );
      worksiteLocationForm.setFieldValue(
        'billing.zip',
        customer.billingLocation?.zip || undefined,
      );
    }, 0);
  };

  const getAssignmentRequestModel = (options: RequestModelType) => {
    const { isDraft } = options;

    const values = getCurrentFormValues();

    const {
      billRate,
      payRate,
      payType,
      profitRate,
      overtime,
      overtimeBillRate,
      overtimePayRate,
      mbpRate,
      multiRates,
      dtBillRate,
      dtPayRate,
      vmsRate,
      mspRate,
      overrideVmsRate,
      overrideMspRate,
    } = mapFinancialInfoToRequest(
      values,
      initialFinancialSchedulerValues,
      predefinedInitialValues,
    );
    const {
      bestPrimaryContacts,
      supervisorInfo,
      timesheetApproverNotApplicable,
      timesheetApproverSameAsSupervisor,
      customerId,
      timesheetApproverInfo,
      timesheetBackupApproverInfo,
    } = isCorporateTA
      ? mapContactInfoToRequest(values.customerInfo as ContactInfoFormValues)
      : mapCustomerInfoFormToRequest(
          values.customerInfo as CustomerInfoFormValues,
          assignmentCreationType,
        );

    const billingContacts = !isCorporateTA
      ? mapCustomerInfoFormToRequest(
          values.customerInfo as CustomerInfoFormValues,
          assignmentCreationType,
        ).billingContacts
      : null;

    const additionalTimesheetApprovers = !isCorporateTA
      ? mapCustomerInfoFormToRequest(
          values.customerInfo as CustomerInfoFormValues,
          assignmentCreationType,
        ).additionalTimesheetApprovers
      : undefined;

    const {
      workSameAsHomeOrBusinessLocation,
      billingSameAsWorkLocation,
      workLocation,
      billingLocation,
    } = mapWorkAndBillingLocationToRequest(values.worksiteLocation);

    const { splitInfo, partnerDraft } = mapSplitFormValuesToRequest(
      values.splitInfo,
    );

    const workerInfo = mapWorkerInformationToRequest(
      values.workerInfo,
      selectedAssignmentType as AssignmentTypeEnum,
      isDraft,
      workSiteWorkLocation,
    );

    const getCustomerId = () => {
      if (hasActiveAssignmentRestriction) return undefined;

      if (!isRehireWithSameCustomer) return customerId;

      return undefined;
    };

    const contractorCompanyInfo =
      selectedAssignmentType !== AssignmentTypeEnum.EEAssignment
        ? mapContractorCompanyInformationToRequest(
            values.workerInfo,
            selectedAssignmentType as AssignmentTypeEnum,
          )
        : undefined;

    const data: AssignmentPrimaryDocumentEditModel = {
      parentAssignmentId:
        isRenewOrEditRenewMode || isRehireOrEditRehireMode
          ? assignmentId
          : undefined,
      assignmentType: selectedAssignmentType as AssignmentTypeEnum,
      assignmentTeammates: mapInternalReportsToRequest(
        values.assignmentInfo.internalReports,
      ),
      assignmentCategory: isCorporateTA
        ? AssignmentCategoryEnum.Corporate
        : AssignmentCategoryEnum.Recruiter,
      customerId: getCustomerId(),
      assignmentInfo: mapAssignmentInfoToRequest(values.assignmentInfo),
      financialInfo: {
        payRate: !isNil(payRate) ? payRate : undefined,
        payType: payType || undefined,
        billRate: !isNil(billRate) ? billRate : undefined,
        vmsFee: !isNil(vmsRate) ? vmsRate : undefined,
        mspFee: !isNil(mspRate) ? mspRate : undefined,
        vmsFeeIsOverridden: !isNil(overrideVmsRate)
          ? overrideVmsRate
          : undefined,
        mspFeeIsOverridden: !isNil(overrideMspRate)
          ? overrideMspRate
          : undefined,
        profitRate: !isNil(profitRate) ? profitRate : undefined,
        overtime: overtime || undefined,
        overtimeBillRate: !isNil(overtimeBillRate)
          ? overtimeBillRate
          : undefined,
        overtimePayRate: !isNil(overtimePayRate) ? overtimePayRate : undefined,
        dtBillRate: !isNil(dtBillRate) ? dtBillRate : undefined,
        dtPayRate: !isNil(dtPayRate) ? dtPayRate : undefined,
      },
      multiRates: multiRates?.length > 0 ? multiRates : undefined,
      supervisorInfo,
      timesheetApproverNotApplicable,
      timesheetApproverSameAsSupervisor,
      additionalTimesheetApprovers,
      timesheetApproverInfo,
      timesheetBackupApproverInfo,
      workSameAsHomeOrBusinessLocation,
      workLocation,
      billingSameAsWorkLocation,
      billingLocation,
      splitInfo,
      partnerDraft,
      contractorCompanyInfo: !hasActiveAssignmentRestriction
        ? contractorCompanyInfo
        : undefined,
    };

    if (
      !hasActiveAssignmentRestriction &&
      assignmentCreationType !== AssignmentCreationTypeEnum.Renew
    ) {
      data.workerInfo = workerInfo;
    }

    if (!(isRecruiter && isRenewMode)) {
      data.bestPrimaryContacts = bestPrimaryContacts;
    }

    if (!isNil(billingContacts)) {
      data.bestBillingContacts = billingContacts;
    }

    (data.assignmentInfo as AssignmentInfoEditModel).customerPurchaseOrder =
      values.assignmentInfo.customerPurchaseOrder || undefined;

    if (data?.financialInfo && isMBPRateToRequestModel(mbpRate)) {
      data.financialInfo.mbpRate = mbpRate as number;
    }

    if (isVisibleTemplateSection) {
      data.templateName = values.template?.templateName;
      data.isNeedCreateTemplate = values.template?.isNeedCreateTemplate;
    }

    if (!isNil(values?.financialScheduler)) {
      data.financialScheduler = mapFinancialInfoSchedulerToRequest(
        values?.financialScheduler,
      );
    }

    if (
      isNil(values?.financialScheduler) &&
      predefinedInitialValues?.financialScheduler
    ) {
      data.financialScheduler = mapFinancialInfoSchedulerToRequest(
        predefinedInitialValues.financialScheduler,
      );
    }

    return data;
  };

  const isMBPRateToRequestModel = (mbpRate?: number | null) => {
    if (!isNil(mbpRate)) {
      if (!isW2Assignment && !isNil(priceAgreement?.icPrice))
        return mbpRate !== priceAgreement?.icPrice;
      if (!isNil(w2InfoByWorkSiteWorkLocation?.w2Price))
        return mbpRate !== w2InfoByWorkSiteWorkLocation?.w2Price;
      return true;
    }
  };

  const onResetFinancialInfoSchedulers = () => {
    setInitialFinancialSchedulerValues(undefined);
    setPredefinedInitialValues((prevState) => {
      if (!prevState) return;

      return {
        ...prevState,
        financialScheduler: undefined,
      };
    });
  };

  const onSetFinancialScheduler = (data: FinancialSchedulerFormValues) => {
    const financialInfoScheduler = merge(
      initialFinancialSchedulerValues?.financialInfoScheduler,
      data.financialInfoScheduler,
    );

    const effectiveDate = !isNil(data.effectiveDate)
      ? data.effectiveDate
      : initialFinancialSchedulerValues?.effectiveDate || undefined;

    const multiRates = mapMultiRatesSchedulerToInitialValues(data.multiRates);

    const newState: FinancialSchedulerFormValues = {
      effectiveDate: effectiveDate,
      financialInfoScheduler: !isEmpty(financialInfoScheduler)
        ? financialInfoScheduler
        : undefined,
      multiRates,
    };

    setInitialFinancialSchedulerValues(newState);
  };

  const onSetPredefinedFinancialScheduler = (
    data: FinancialSchedulerFormValues,
  ) => {
    if (!predefinedInitialValues) return;

    const { financialScheduler: predefinedScheduler } = predefinedInitialValues;
    const {
      financialInfoScheduler: dataScheduler,
      effectiveDate: dataDate,
      multiRates: dataMultiRates,
    } = data;

    const financialInfoScheduler =
      predefinedScheduler?.financialInfoScheduler || dataScheduler
        ? merge(predefinedScheduler?.financialInfoScheduler, dataScheduler)
        : undefined;

    const effectiveDate =
      dataDate || predefinedScheduler?.effectiveDate || undefined;

    const multiRates = mapMultiRatesSchedulerToInitialValues(dataMultiRates);

    const financialScheduler: FinancialSchedulerFormValues = {
      financialInfoScheduler,
      effectiveDate,
      multiRates,
    };

    setPredefinedInitialValues({
      ...predefinedInitialValues,
      financialScheduler: financialScheduler,
    });

    setInitialFinancialSchedulerValues({
      ...initialFinancialSchedulerValues,
      ...financialScheduler,
    });
  };

  const handleFinancialSchedulerSave = async () => {
    await handleSubmit();
  };

  return (
    <>
      {isFinancialInfoSchedulerModalVisible && (
        <FinancialInfoScheduleModal
          mode={EffectiveDateMode.ADD}
          initialValues={initialFinancialSchedulerValues}
          onSuccess={handleFinancialSchedulerSave}
          onClose={handleCloseFinancialSchedulerModal}
          mspOrVmsOverviewInfo={selectedCustomer?.mspOrVmsOverviewInfo}
          externalRef={financialSchedulerFormRef}
          w2InfoByWorkSiteWorkLocation={w2InfoByWorkSiteWorkLocation}
          isW2Assignment={isW2Assignment}
          workStateLocation={workSiteWorkLocation?.state}
          assignmentType={selectedAssignmentType}
          ptoMultiRate={getPTOMultiRateDetails(
            payCodesDetails,
            mapMultiRates(
              (financialInfoFormRef?.current as FinancialInfoFormikProps)
                ?.values?.multiRates,
            ),
          )}
          jobCategoryValue={
            (assignmentInfoFormRef?.current as AssignmentInfoFormikProps)
              ?.values?.jobCategory
          }
        />
      )}
      <BaseModal
        title={getModalTitle(assignmentCreationType)}
        maxWidth={888}
        open={true}
        className={styles.assignmentModal}
        onCancel={handleModalCancel}
      >
        <Steps direction="horizontal" className="custom-steps">
          {isTypeSelectorStepVisible && (
            <Step
              icon={
                <Tooltip title={tabs[TabKeys.TypeSelector].title}>
                  <span className="icon icon-assignment-type" />
                </Tooltip>
              }
              onClick={() => handleTabChange(TabKeys.TypeSelector)}
              className={cx(
                getTabClassNames(
                  TabKeys.TypeSelector,
                  getTabStatus(TabKeys.TypeSelector),
                ),
              )}
              status={getTabStatus(TabKeys.TypeSelector)}
            />
          )}
          <Step
            icon={
              <Tooltip title={tabs[TabKeys.WorkerInfo].title}>
                <span className="icon icon-employee-information" />
              </Tooltip>
            }
            onClick={() => handleTabChange(TabKeys.WorkerInfo)}
            className={cx(
              getTabClassNames(
                TabKeys.WorkerInfo,
                getTabStatus(TabKeys.WorkerInfo),
              ),
            )}
            status={getTabStatus(TabKeys.WorkerInfo)}
          />
          <Step
            icon={
              <Tooltip title={tabs[TabKeys.CustomerInfo].title}>
                <span className="icon icon-customer-information" />
              </Tooltip>
            }
            onClick={() => handleTabChange(TabKeys.CustomerInfo)}
            className={cx(
              getTabClassNames(
                TabKeys.CustomerInfo,
                getTabStatus(TabKeys.CustomerInfo),
              ),
            )}
            status={getTabStatus(TabKeys.CustomerInfo)}
          />
          <Step
            icon={
              <Tooltip title={tabs[TabKeys.WorksiteLocation].title}>
                <span className="icon icon-worksite-location" />
              </Tooltip>
            }
            onClick={() => handleTabChange(TabKeys.WorksiteLocation)}
            className={cx(
              getTabClassNames(
                TabKeys.WorksiteLocation,
                getTabStatus(TabKeys.WorksiteLocation),
              ),
            )}
            status={getTabStatus(TabKeys.WorksiteLocation)}
          />
          <Step
            icon={
              <Tooltip title={tabs[TabKeys.AssignmentInfo].title}>
                <span className="icon icon-assignment-information" />
              </Tooltip>
            }
            onClick={() => handleTabChange(TabKeys.AssignmentInfo)}
            className={cx(
              getTabClassNames(
                TabKeys.AssignmentInfo,
                getTabStatus(TabKeys.AssignmentInfo),
              ),
            )}
            status={getTabStatus(TabKeys.AssignmentInfo)}
          />
          <Step
            icon={
              <Tooltip title={tabs[TabKeys.FinancialInfo].title}>
                <span className="icon icon-financial-information" />
              </Tooltip>
            }
            onClick={() => handleTabChange(TabKeys.FinancialInfo)}
            className={cx(
              getTabClassNames(
                TabKeys.FinancialInfo,
                getTabStatus(TabKeys.FinancialInfo),
              ),
            )}
            status={getTabStatus(TabKeys.FinancialInfo)}
          />
          <Step
            icon={
              <Tooltip title={tabs[TabKeys.SplitInfo].title}>
                <span className="icon icon-split-information" />
              </Tooltip>
            }
            onClick={() => handleTabChange(TabKeys.SplitInfo)}
            className={cx(
              getTabClassNames(
                TabKeys.SplitInfo,
                getTabStatus(TabKeys.SplitInfo),
              ),
            )}
            status={getTabStatus(TabKeys.SplitInfo)}
          />
        </Steps>

        <Tabs
          activeKey={`${activeTab}`}
          animated={false}
          className={styles.assignmentTab}
        >
          <Tabs.TabPane forceRender key={`${TabKeys.TypeSelector}`} tab={null}>
            <AssignmentTypeSelection
              formRef={typeSelectionFormRef}
              onChange={onFormChange[TabKeys.TypeSelector]}
              initialValues={{ assignmentType: selectedAssignmentType }}
              onAssignmentTypeChange={handleAssignmentTypeChange}
            />
          </Tabs.TabPane>

          <Tabs.TabPane forceRender key={`${TabKeys.WorkerInfo}`} tab={null}>
            {isW2Assignment ? (
              <W2WorkerInfo
                workers={workers}
                isWorkersLoaded={isWorkersLoaded}
                formRef={workerInfoFormRef}
                onChange={onFormChange[TabKeys.WorkerInfo]}
                initialValues={predefinedInitialValues?.workerInfo}
                isRenewOrEditRenewMode={isRenewOrEditRenewMode}
                isRehireOrEditRehireMode={isRehireOrEditRehireMode}
                hasActiveAssignmentRestriction={hasActiveAssignmentRestriction}
                addresses={addresses}
                onAddressChange={handleAddressChange}
              />
            ) : selectedAssignmentType === AssignmentTypeEnum.ICAssignment ? (
              <ICWorkerInfo
                workers={workers}
                isWorkersLoaded={isWorkersLoaded}
                companies={companies}
                isCompaniesLoaded={isCompaniesLoaded}
                fileInfo={workerFileInfo}
                formRef={workerInfoFormRef}
                onChange={onFormChange[TabKeys.WorkerInfo]}
                assignmentType={AssignmentTypeEnum.ICAssignment}
                initialValues={predefinedInitialValues?.workerInfo}
                isRenewOrEditRenewMode={isRenewOrEditRenewMode}
                isRehireOrEditRehireMode={isRehireOrEditRehireMode}
                hasActiveAssignmentRestriction={hasActiveAssignmentRestriction}
                addresses={addresses}
                onAddressChange={handleAddressChange}
              />
            ) : (
              <SCWorkerInfo
                workers={workers}
                isWorkersLoaded={isWorkersLoaded}
                companies={companies}
                isCompaniesLoaded={isCompaniesLoaded}
                formRef={workerInfoFormRef}
                fileInfo={contractorCompanyFileInfo}
                onChange={onFormChange[TabKeys.WorkerInfo]}
                assignmentType={AssignmentTypeEnum.SCAssignment}
                initialValues={predefinedInitialValues?.workerInfo}
                isRenewOrEditRenewMode={isRenewOrEditRenewMode}
                isRehireOrEditRehireMode={isRehireOrEditRehireMode}
                hasActiveAssignmentRestriction={hasActiveAssignmentRestriction}
                addresses={addresses}
                onAddressChange={handleAddressChange}
              />
            )}
          </Tabs.TabPane>

          <Tabs.TabPane forceRender key={`${TabKeys.CustomerInfo}`} tab={null}>
            {isCorporateTA ? (
              <ContactInfo
                formRef={customerInfoFormRef}
                onChange={onFormChange[TabKeys.CustomerInfo]}
                initialValues={
                  predefinedInitialValues?.customerInfo as ContactInfoFormValues
                }
                companyId={companyId}
              />
            ) : (
              <CustomerInfo
                formRef={customerInfoFormRef}
                customers={customers}
                selectedCustomer={selectedCustomer}
                onChange={onFormChange[TabKeys.CustomerInfo]}
                onCustomerChange={handleCustomerChange}
                initialValues={
                  predefinedInitialValues?.customerInfo as CustomerInfoFormValues
                }
                isRenewOrEditRenewMode={isRenewOrEditRenewMode}
                isRehireOrEditRehireMode={isRehireOrEditRehireMode}
                isRehireWithNewCustomer={isRehireWithNewCustomer}
                isCustomersLoaded={isCustomersLoaded}
                hasActiveAssignmentRestriction={hasActiveAssignmentRestriction}
              />
            )}
          </Tabs.TabPane>

          <Tabs.TabPane
            forceRender
            key={`${TabKeys.WorksiteLocation}`}
            tab={null}
          >
            <WorksiteLocation
              formRef={worksiteLocationFormRef}
              onChange={onFormChange[TabKeys.WorksiteLocation]}
              mainLocation={workerLocation}
              mainLocationLabel={
                isW2Assignment
                  ? 'Same as home location'
                  : 'Same as business location'
              }
              initialValues={predefinedInitialValues?.worksiteLocation}
              disabled={
                isCorporateTA
                  ? false
                  : !customerInfoFormRef?.current?.values?.generalInfo?.customer
              }
              addresses={addresses}
              onAddressChange={handleAddressChange}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            forceRender
            key={`${TabKeys.AssignmentInfo}`}
            tab={null}
          >
            <AssignmentInfo
              companyId={companyId}
              formRef={assignmentInfoFormRef}
              onChange={onFormChange[TabKeys.AssignmentInfo]}
              initialValues={predefinedInitialValues?.assignmentInfo}
              isEmploymentTypeVisible={isW2Assignment}
              assignmentStatus={assignmentStatus}
              hasRenewModeAndDateRestriction={hasRenewModeAndDateRestriction}
              hasRehireModeAndDateRestriction={hasRehireModeAndDateRestriction}
              selectedCustomer={selectedCustomer}
              w2InfoList={w2InfoList}
              workSiteWorkLocation={workSiteWorkLocation}
              isRenewOrRehireMode={isRenewOrRehireMode}
              updateAssignmentValuesWithFilteredMultiRates={
                updateAssignmentValuesWithFilteredMultiRates
              }
            />
          </Tabs.TabPane>

          <Tabs.TabPane forceRender key={`${TabKeys.FinancialInfo}`} tab={null}>
            <FinancialInfo
              formRef={financialInfoFormRef}
              mspOrVmsOverviewInfo={selectedCustomer?.mspOrVmsOverviewInfo}
              workSiteWorkLocation={workSiteWorkLocation}
              onChange={onFormChange[TabKeys.FinancialInfo]}
              initialValues={predefinedInitialValues?.financialInfo}
              selectedAssignmentType={selectedAssignmentType}
              w2InfoByWorkSiteWorkLocation={w2InfoByWorkSiteWorkLocation}
              predefinedFinancialSchedulerValues={
                predefinedInitialValues?.financialScheduler
              }
              initialMBPRate={initialMBPRate}
              onSetPredefinedFinancialScheduler={
                onSetPredefinedFinancialScheduler
              }
              onResetFinancialInfoSchedulers={onResetFinancialInfoSchedulers}
              assignmentInfoForm={assignmentInfoFormRef?.current}
            />
          </Tabs.TabPane>

          <Tabs.TabPane forceRender key={`${TabKeys.SplitInfo}`} tab={null}>
            <SplitInfo
              formRef={splitInfoFormRef}
              onChange={onFormChange[TabKeys.SplitInfo]}
              initialValues={predefinedInitialValues?.splitInfo}
              memberId={memberId}
            />
          </Tabs.TabPane>
        </Tabs>

        <BaseModalFooter className="button-space-between">
          <div className={cx('d-flex', styles.growBox)}>
            {isDraftAvailable && (
              <Button
                onClick={handleDraftSubmit}
                type="primary"
                disabled={!isDraftValid}
              >
                Save as draft
              </Button>
            )}
            <Button
              className="with-icon modal-icon-btn"
              onClick={handleModalCancel}
            >
              <span className="icon icon-closed-outlined" />
              Cancel
            </Button>
            {isVisibleTemplateSection && (
              <AssignmentTemplate
                formRef={templateFormRef}
                setIsTemplateErrors={setIsTemplateErrors}
                isTemplateEditMode={isTemplateEditMode}
                initialValues={predefinedInitialValues?.template}
              />
            )}
          </div>

          <div className="d-flex">
            {activeTab !==
              (isTypeSelectorStepVisible
                ? TabKeys.TypeSelector
                : TabKeys.WorkerInfo) && (
              <Button
                className="with-icon modal-icon-btn"
                onClick={handlePrevStep}
              >
                <span className="icon icon-double-left" />
                Back
              </Button>
            )}

            {activeTab < tabs.length - 1 && (
              <Button
                type="primary"
                className="with-icon"
                onClick={handleNextStep}
              >
                Next <span className="icon icon-double-right" />
              </Button>
            )}

            {activeTab === tabs.length - 1 && (
              <Button
                type="primary"
                data-id="createNewAssignmentBtn"
                disabled={isLoading}
                loading={isLoading}
                onClick={onAssignmentSubmit}
              >
                {getModalSubmitName(assignmentCreationType)}
              </Button>
            )}
          </div>
        </BaseModalFooter>
      </BaseModal>
    </>
  );
}
